import * as api from "app/api/users"
import * as planningApi from "app/api/planning"
import User, { ContractTypes, Roles } from "app/models/user"
import { SagaIterator } from "redux-saga"
import { all, call, put, select, takeLatest } from "redux-saga/effects"
import { history } from "store"
import { handleError } from "app/redux/errorHandler/errorHandler.saga"
import WorkingHour from "app/models/workingHour"
import { arraysIntersect } from "utils"
import * as notificationActions from "../notifications/notifications.actions"
import * as actions from "./users.actions"
import { PatchUsersAction } from "./users.actions"
import * as balanceHoursCorrectionsActions from "../balanceHoursCorrections/balanceHoursCorrections.actions"
import * as contractActions from "../contracts/contracts.actions"
import * as currentUserActions from "../currentUser/currentUser.actions"
import * as currentUserSelectors from "../currentUser/currentUser.selectors"
import * as selectors from "./users.selectors"
import { putPreferedHoursSuccess } from "../preferedHours/preferedHours.actions"
import { setDocumentLanguage } from "../utils"
import { getAccessRoles } from "../authorization/authorization.selectors"

const endpoint = "users"
const service = "users"

export function* getUsersFlow(action: actions.GetUsersAction): SagaIterator {
  try {
    const { params } = action.payload
    const storeParams = yield select(selectors.getParams)
    const response = yield call(api.getUsers, params || storeParams)
    yield put(actions.getUsersSuccess(response && response.data))
  } catch (e) {
    yield put(actions.getUsersFail(e))
    yield call(handleError, { endpoint, service, error: e })
  }
}

export function* deleteUsersByIdFlow(action: actions.DeleteUsersByIdAction): SagaIterator {
  try {
    const { id } = action.payload
    yield call(api.deleteUsersById, id)
    yield put(actions.deleteUsersByIdSuccess())
    yield put(
      notificationActions.show({
        id: "deleteUserSuccess",
        message: "confirmationDialog.employees.delete.message",
        variant: "success",
      }),
    )
    history.push("/employees")
    yield put(actions.getUsers())
  } catch (e) {
    yield put(actions.deleteUsersByIdFail(e))
    yield call(handleError, { endpoint, service, error: e })
  }
}

export function* postUsersFlow(action: actions.PostUsersAction): SagaIterator {
  try {
    const {
      user,
      correction,
      contract,
      user: { preferable_working_hours },
    } = action.payload
    const {
      data: { id },
    } = yield call(api.postUsers, user)
    if (preferable_working_hours?.length) {
      const groupedHoursByWeekday = preferable_working_hours
        .reduce(
          (result: WorkingHour[][], pwh: WorkingHour) => {
            const newResult = result
            newResult[pwh.weekday] = result[pwh.weekday] ? [...result[pwh.weekday], pwh] : [pwh]
            return [...newResult]
          },
          [...Array(7).fill([])],
        )
        .filter(item => item.length)
        .map(pwhGroup => call(api.putUserWorkingHoursById, { userId: id, data: pwhGroup }))
      yield all(groupedHoursByWeekday)
    }
    yield put(actions.postUsersSuccess())
    if (correction) {
      yield call(planningApi.postBalanceHoursCorrection, id, correction)
      yield put(balanceHoursCorrectionsActions.postBalanceHoursCorrectionsSuccess())
    }
    if (contract) {
      yield call(planningApi.postContract, id, contract)
      yield put(contractActions.postContractSuccess())
    }
    yield put(actions.getUsersById(id))
    yield put(actions.getUsers())
    yield put(
      notificationActions.show({
        id: "postUser",
        message: "confirmationDialog.employees.create.message",
        variant: "success",
      }),
    )
  } catch (e) {
    yield put(actions.postUsersFail(e))
    yield put(balanceHoursCorrectionsActions.postBalanceHoursCorrectionsFail(e))
    yield put(contractActions.postContractFail(e))
    yield call(handleError, { endpoint, service, error: e })
  }
}

export function* getUsersByIdFlow(action: actions.GetUsersByIdAction): SagaIterator {
  try {
    const response = yield call(api.getUsersById, action.payload.id)
    const data: User = response && response.data

    const accessRoles: Roles[] = yield select(getAccessRoles)
    const isAllowed = arraysIntersect([Roles.Planner, Roles.HROfficer], accessRoles)
    if (isAllowed) {
      const { data: timetables } = yield call(planningApi.getTimetables, data.id)
      yield put(putPreferedHoursSuccess(timetables))
    }
    yield put(actions.getUsersByIdSuccess(data))
  } catch (e) {
    yield put(actions.getUsersByIdFail(e))
    yield call(handleError, { endpoint, service, error: e })
  }
}

export function* patchUsersFlow(action: PatchUsersAction): SagaIterator {
  try {
    const user: User = yield select(selectors.getUser)
    if (user.contract_type === ContractTypes.EXTERNAL) {
      delete user.contracted_hours
    }
    const { field, value } = action.payload
    const { settings: __, contract_start_date: _, ...updatedUser } = user
    const newUser = { ...updatedUser, [field]: value }
    const response = yield call(api.patchUsers, newUser)
    const data: User = response && response.data
    yield put(actions.patchUsersSuccess(data))
    const currentUser = yield select(currentUserSelectors.getCurrentUser)
    if (String(data?.id) === String(currentUser?.id)) {
      yield put(currentUserActions.getCurrentUserSuccess(data))
      const { locale } = data
      yield call(setDocumentLanguage, locale)
    }
  } catch (e) {
    yield put(actions.patchUsersFail(e))
    yield call(handleError, { endpoint, service, error: e })
  }
}

export default function* usersSaga(): SagaIterator {
  yield takeLatest(actions.Types.GET, getUsersFlow)
  yield takeLatest(actions.Types.DELETE, deleteUsersByIdFlow)
  yield takeLatest(actions.Types.POST, postUsersFlow)
  yield takeLatest(actions.Types.GET_BY_ID, getUsersByIdFlow)
  yield takeLatest(actions.Types.PATCH, patchUsersFlow)
}
